// ════════════════════════════════════════════════════════════════════════════
//  features/fiscal/transporte/services/mdfeService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type {
  MDFeResumoDto,
  PaginadoMDFeResponse,
  EmitirMDFeRequest,
  EncerrarMDFeRequest,
  CancelarMDFeRequest,
  TransmitirMDFeResponse,
  ListaTransporteFiltros,
} from '../types';
import { paramsDaLista } from '../types';

const BASE = '/api/mdfe';

export const mdfeService = {
  /** Lista paginada; busca, situação, período de emissão e ordem são feitos no servidor. */
  listar: (pagina = 1, tamanhoPagina = 20, filtros: ListaTransporteFiltros = {}) =>
    apiClient
      .get<PaginadoMDFeResponse>(BASE, { params: { pagina, tamanhoPagina, ...paramsDaLista(filtros) } })
      .then((r) => r.data),

  obterPorId: (id: number) =>
    apiClient.get<MDFeResumoDto>(`${BASE}/${id}`).then((r) => r.data),

  emitir: (req: EmitirMDFeRequest) =>
    apiClient.post<TransmitirMDFeResponse>(`${BASE}/emitir`, req).then((r) => r.data),

  encerrar: (id: number, req: EncerrarMDFeRequest) =>
    apiClient.post<TransmitirMDFeResponse>(`${BASE}/${id}/encerrar`, req).then((r) => r.data),

  cancelar: (id: number, req: CancelarMDFeRequest) =>
    apiClient.post<TransmitirMDFeResponse>(`${BASE}/${id}/cancelar`, req).then((r) => r.data),

  /** Transmite de novo o MESMO manifesto (rejeitado ou sem resposta), mantendo o número. */
  transmitir: (id: number) =>
    apiClient.post<TransmitirMDFeResponse>(`${BASE}/${id}/transmitir`).then((r) => r.data),

  /** Abre o DAMDFE (PDF) em uma nova aba. Vai pelo apiClient: precisa do login e do endereço da API. */
  async abrirDamdfe(id: number): Promise<void> {
    const { data } = await apiClient.get<Blob>(`${BASE}/${id}/damdfe`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    // O navegador precisa da URL viva enquanto carrega o PDF na aba nova.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  /** Baixa o XML autorizado (procMDFe) ou, com `evento`, o do encerramento/cancelamento. */
  async baixarXml(id: number, chave: string | null, evento?: 'encerramento' | 'cancelamento'): Promise<void> {
    const { data } = await apiClient.get<Blob>(`${BASE}/${id}/xml`, {
      params: evento ? { evento } : undefined,
      responseType: 'blob',
    });
    const url  = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${chave ?? `mdfe-${id}`}-${evento ?? 'procMDFe'}.xml`;
    link.click();
    URL.revokeObjectURL(url);
  },
};
